import React, { useEffect } from "react";
import Lenis from "lenis";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingWidgets from "@/components/FloatingWidgets";
import ChatWidget from "@/components/ChatWidget";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Products from "@/pages/Products";
import ProductDetail from "@/pages/ProductDetail";
import WhyOrvella from "@/pages/WhyOrvella";
import Quality from "@/pages/Quality";
import ExportProcess from "@/pages/ExportProcess";
import Markets from "@/pages/Markets";
import PrivateLabel from "@/pages/PrivateLabel";
import Insights from "@/pages/Insights";
import BlogPost from "@/pages/BlogPost";
import Contact from "@/pages/Contact";
import Quote from "@/pages/Quote";
import Catalogue from "@/pages/Catalogue";
import PayPage from "@/pages/PayPage";
import Legal from "@/pages/Legal";
import NotFound from "@/pages/NotFound";
import AdminApp from "@/pages/admin/AdminApp";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(err) {
    console.error("UI error:", err);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-ivory px-6 text-center">
          <div>
            <h1 className="font-serif text-2xl text-navy">Something went wrong</h1>
            <p className="mt-2 text-mist">Please refresh the page. If the issue persists, contact info@orvellaoverseas.com.</p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [pathname]);
  return null;
}

function SmoothScroll() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.05, smoothWheel: true });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);
  return null;
}

function SiteLayout({ children }) {
  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
      <FloatingWidgets />
      <ChatWidget />
    </div>
  );
}

const PublicPage = ({ children }) => <SiteLayout>{children}</SiteLayout>;

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<PublicPage><Home /></PublicPage>} />
          <Route path="/about" element={<PublicPage><About /></PublicPage>} />
          <Route path="/products" element={<PublicPage><Products /></PublicPage>} />
          <Route path="/products/:slug" element={<PublicPage><ProductDetail /></PublicPage>} />
          <Route path="/why-orvella" element={<PublicPage><WhyOrvella /></PublicPage>} />
          <Route path="/quality" element={<PublicPage><Quality /></PublicPage>} />
          <Route path="/export-process" element={<PublicPage><ExportProcess /></PublicPage>} />
          <Route path="/markets" element={<PublicPage><Markets /></PublicPage>} />
          <Route path="/private-label" element={<PublicPage><PrivateLabel /></PublicPage>} />
          <Route path="/insights" element={<PublicPage><Insights /></PublicPage>} />
          <Route path="/insights/:slug" element={<PublicPage><BlogPost /></PublicPage>} />
          <Route path="/contact" element={<PublicPage><Contact /></PublicPage>} />
          <Route path="/quote" element={<PublicPage><Quote /></PublicPage>} />
          <Route path="/catalogue" element={<PublicPage><Catalogue /></PublicPage>} />
          <Route path="/pay/:id" element={<PublicPage><PayPage /></PublicPage>} />
          <Route path="/privacy-policy" element={<PublicPage><Legal kind="privacy" /></PublicPage>} />
          <Route path="/terms-conditions" element={<PublicPage><Legal kind="terms" /></PublicPage>} />
          <Route path="/cookie-policy" element={<PublicPage><Legal kind="cookies" /></PublicPage>} />
          <Route path="/login" element={<AdminApp />} />
          <Route path="/admin" element={<AdminApp />} />
          <Route path="/admin/*" element={<AdminApp />} />
          <Route path="*" element={<PublicPage><NotFound /></PublicPage>} />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
